import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { staffApi } from "@client/api/staff";
import type { Result } from "@client/api/types";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { useConfirm } from "@client/components/confirm-dialog";
import { Input } from "@client/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@client/components/ui/select";

export const Route = createFileRoute(
  "/_authenticated/results/session/$sessionId",
)({
  component: SessionResults,
});

type SortKey = "name" | "score" | "outcome";

function SessionResults() {
  const { sessionId } = Route.useParams();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: results } = useQuery({
    queryKey: ["results"],
    queryFn: staffApi.results,
  });

  const [search, setSearch] = useState("");
  const [outcome, setOutcome] = useState<"all" | "pass" | "fail">("all");
  const [sort, setSort] = useState<SortKey>("score");

  const deleteResultMut = useMutation({
    mutationFn: (id: string) => staffApi.deleteResult(id),
    onSuccess: () => {
      toast.success("Result deleted successfully");
      qc.invalidateQueries({ queryKey: ["results"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sessionResults = useMemo(
    () => (results ?? []).filter((r) => r.session_id === sessionId),
    [results, sessionId],
  );

  const title = sessionResults[0]?.exam_sessions?.title ?? "Session results";

  const stats = useMemo(() => {
    const count = sessionResults.length;
    const passed = sessionResults.filter((r) => r.passed).length;
    const avg =
      count === 0
        ? 0
        : sessionResults.reduce((s, r) => s + Number(r.percentage), 0) / count;
    return { count, passed, failed: count - passed, avg };
  }, [sessionResults]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = sessionResults.filter((r) => {
      const a = r.referee_attempts;
      if (outcome === "pass" && !r.passed) return false;
      if (outcome === "fail" && r.passed) return false;
      if (q) {
        if (!(a?.full_name ?? "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
    return filtered.sort((a, b) => {
      if (sort === "name") {
        return (a.referee_attempts?.full_name ?? "").localeCompare(
          b.referee_attempts?.full_name ?? "",
        );
      }
      if (sort === "outcome") {
        return Number(b.passed) - Number(a.passed);
      }
      return Number(b.percentage) - Number(a.percentage);
    });
  }, [sessionResults, search, outcome, sort]);

  async function handleDelete(id: string, name?: string) {
    if (
      await confirm({
        title: "Delete result?",
        description: `Delete the result${name ? ` for "${name}"` : ""}? This action cannot be undone.`,
        confirmText: "Delete",
        destructive: true,
      })
    ) {
      deleteResultMut.mutate(id);
    }
  }

  function exportCsv() {
    const header = [
      "Name",
      "License",
      "Country",
      "Version",
      "Correct",
      "Wrong",
      "Unanswered",
      "Percentage",
      "Outcome",
    ];
    const body = rows.map((r) => {
      const a = r.referee_attempts;
      return [
        a?.full_name ?? "",
        a?.license_id ?? "",
        a?.country ?? "",
        a?.version_label ?? "",
        r.correct_count,
        r.wrong_count,
        r.unanswered_count,
        Number(r.percentage).toFixed(2),
        r.passed ? "PASS" : "FAIL",
      ];
    });
    const csv = [header, ...body]
      .map((r) =>
        r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-results.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const passRate =
    stats.count === 0 ? 0 : Math.round((stats.passed / stats.count) * 100);

  return (
    <StaffShell
      title={title}
      subtitle={`${stats.count} candidate${stats.count === 1 ? "" : "s"} graded`}
      showBack
      actions={
        sessionResults.length > 0 ? (
          <button
            onClick={exportCsv}
            className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-secondary"
          >
            Export CSV
          </button>
        ) : null
      }
    >
      {/* Stat strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Candidates" value={String(stats.count)} />
        <StatCard
          label="Pass rate"
          value={`${passRate}%`}
          tone={passRate >= 50 ? "good" : "bad"}
        />
        <StatCard label="Avg score" value={`${stats.avg.toFixed(0)}%`} />
        <StatCard label="Failed" value={String(stats.failed)} />
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 w-full sm:max-w-xs"
        />
        <Select value={outcome} onValueChange={(v) => setOutcome(v as never)}>
          <SelectTrigger className="h-9 w-32">
            <SelectValue placeholder="Outcome" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="pass">Pass</SelectItem>
            <SelectItem value="fail">Fail</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
          <SelectTrigger className="h-9 w-36">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="score">Sort: Score</SelectItem>
            <SelectItem value="name">Sort: Name</SelectItem>
            <SelectItem value="outcome">Sort: Outcome</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="panel overflow-x-auto p-5">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-2">Referee</th>
              <th>Version</th>
              <th>Correct</th>
              <th>Wrong</th>
              <th>Unanswered</th>
              <th className="min-w-[140px]">Score</th>
              <th>Outcome</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ResultRow
                key={r.id}
                result={r}
                onDelete={handleDelete}
                deleting={deleteResultMut.isPending}
              />
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {sessionResults.length === 0
              ? "No graded attempts in this session."
              : "No results match the current filters."}
          </p>
        ) : null}
      </div>
    </StaffShell>
  );
}

function ResultRow({
  result: r,
  onDelete,
  deleting,
}: {
  result: Result;
  onDelete: (id: string, name?: string) => void;
  deleting: boolean;
}) {
  const a = r.referee_attempts;
  const pct = Number(r.percentage);
  return (
    <tr className="border-t border-border">
      <td className="py-2">
        <p className="font-medium">{a?.full_name}</p>
        <p className="text-xs text-muted-foreground">{a?.license_id}</p>
      </td>
      <td>
        {a?.version_label ? (
          <Badge variant="outline" className="font-semibold">
            Version {a.version_label}
          </Badge>
        ) : (
          "—"
        )}
      </td>
      <td>{r.correct_count}</td>
      <td>{r.wrong_count}</td>
      <td>{r.unanswered_count}</td>
      <td>
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
            <div
              className={
                r.passed ? "h-full bg-primary" : "h-full bg-destructive"
              }
              style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            />
          </div>
          <span className="tabular-nums">{pct.toFixed(1)}%</span>
        </div>
      </td>
      <td>
        <Badge variant={r.passed ? "default" : "destructive"}>
          {r.passed ? "PASS" : "FAIL"}
        </Badge>
      </td>
      <td className="flex items-center justify-end gap-3 py-2 text-right">
        <Link
          to="/results/$attemptId"
          params={{ attemptId: r.attempt_id }}
          className="text-xs underline"
        >
          Details
        </Link>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => onDelete(r.id, a?.full_name)}
          disabled={deleting}
        >
          Delete
        </Button>
      </td>
    </tr>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  return (
    <div className="panel p-4">
      <p
        className={
          tone === "good"
            ? "text-2xl font-bold tabular-nums text-primary"
            : tone === "bad"
              ? "text-2xl font-bold tabular-nums text-destructive"
              : "text-2xl font-bold tabular-nums"
        }
      >
        {value}
      </p>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}
